import { useNavigate } from 'react-router-dom';
import { Switch } from '@headlessui/react';
import { Info, MoreVertical, Edit, Copy, File as FileExport, Trash2, Import, Plus, ChevronDown, FolderInput, CircleUser } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ParameterTag } from '@/components/autopilot/parameter-tag';
import { TableTag } from '@/components/autopilot/table-tag';
import { useEffect, useState } from 'react';
import { useAutomations } from '@/api/hooks/useAutomation';
import { App, message } from 'antd';
import { SUCCESS_MESSAGE } from '@/utils/constants';
import { deleteAutomation, duplicateAutomation, editAutomation } from '@/api/automation';
import { COLOR } from '@/styles/constants';

interface Automation {
  id: string;
  name: string;
  enabled: boolean;
  parameters: string[];
  blueprint: string;
  profiles: string[];
  lastTriggered: string;
}

const parameterLabels: Record<string, string> = {
  'pmv': 'Pmv',
  'occupancy': 'Occupancy',
  'indoor-humidity': 'Indoor Humidity',
  'indoor-temp': 'Indoor Temp',
  'outdoor-humidity': 'Outdoor Humidity',
  'outdoor-temp': 'Outdoor Temp',
  'solar': 'Solar Surplus Rate',
};

export function AutomationList() {
  const [automations, setAutomations] = useState([])
  const { message } = App.useApp();

  const { data: automationsData, isLoading: automationsLoading, refetch } = useAutomations();
  const navigate = useNavigate();

  useEffect(() => {
    if (!automationsData) return;
    setAutomations(automationsData?.map(automation => ({
      id: automation.id,
      name: automation.name,
      enabled: automation.enable_status,
      parameters: automation.blueprint_parameters.map(param => param.name),
      blueprint: automation.blueprint?.name,
      profiles: automation.used_by_profiles.map(v => v.profile_name),
      createdBy: automation.created_by?.username || 'Unknown',
      lastTriggered: automation.last_triggered,
      export: automation,
    })))
  }, [automationsData])

  const handleStatusChange = async (id, status, idx) => {
    try {
      await editAutomation(id, {
        enable_status: status,
      });
      // refetch()
      message.open({
        type: 'success',
        content: SUCCESS_MESSAGE()
      });
    } catch (e) {
      const newAutomations = [...automations]
      newAutomations[idx].enabled = !e
      setAutomations(newAutomations)
      message.open({
        type: 'error',
        content: (e as Error).message
      });
    }
  }

  const handleDuplicate = async (id) => {
    try {
      await duplicateAutomation(id);
      refetch()
      message.open({
        type: 'success',
        content: SUCCESS_MESSAGE()
      });
    } catch (e) {
      message.open({
        type: 'error',
        content: (e as Error).message
      });
    }
  }

  const handleDelete = async (id) => {
    try {
      await deleteAutomation(id);
      refetch()
      message.open({
        type: 'success',
        content: SUCCESS_MESSAGE()
      });
    } catch (e) {
      message.open({
        type: 'error',
        content: (e as Error).message
      });
    }
  }

  const handleExport = (data) => {
    const jsonData = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${data?.name}`;
    link.click();
  }

  useEffect(() => {
    refetch()
  }, [navigate])

  if (automationsLoading) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex justify-between items-center mb-2">
          <div className="h-6 w-32 bg-gray-200 rounded animate-pulse" />
          <div className="flex gap-2">
            <div className="h-9 w-20 bg-gray-200 rounded animate-pulse" />
            <div className="h-9 w-20 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>
        
        <div className="flex gap-2 items-center">
          <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
          <div className="flex gap-1">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-6 w-16 bg-gray-200 rounded animate-pulse" />
            ))}
          </div>
        </div>

        <div className="px-1">
          <div className="border border-white-blue rounded-[8px] overflow-hidden">
            <div className="bg-light-grey p-4">
              <div className="grid grid-cols-7 gap-4">
                {Array.from({ length: 7 }).map((_, i) => (
                  <div key={i} className="h-4 bg-gray-200 rounded animate-pulse" />
                ))}
              </div>
            </div>
            <div className="divide-y divide-light-grey">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="p-4 grid grid-cols-7 gap-4 items-center">
                  <div className="h-4 w-16 bg-gray-200 rounded animate-pulse" />
                  <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                  <div className="flex gap-1">
                    <div className="h-6 w-12 bg-gray-200 rounded animate-pulse" />
                    <div className="h-6 w-12 bg-gray-200 rounded animate-pulse" />
                  </div>
                  <div className="h-6 w-20 bg-gray-200 rounded animate-pulse" />
                  <div className="h-6 w-16 bg-gray-200 rounded animate-pulse" />
                  <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                  <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">

      <div className="flex justify-between items-center mb-2">
        <h2 className="heading-6 text-dark-blue">Automations</h2>
        <div className="flex gap-2">
          <Button disabled variant="outline" size="md" className="border-2 border-primary shadow-button-light text-primary">
            <FolderInput className="h-4 w-4 mr-2" />
            <p className="button-s">Import</p>
          </Button>
          <Button
            variant="default"
            size="md"
            className="bg-primary text-white"
            onClick={() => navigate('/settings/automations/create')}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            <p className="button-s">Create</p>
          </Button>
        </div>
      </div>

      <div className="flex gap-2 items-center">
        <p className="paragraph-9 text-dark-grey">Parameters Used</p>
        {Object.keys(parameterLabels).map((param) => (
          <ParameterTag key={param} parameter={param} />
        ))}
      </div>

      <div className="px-1">
        <div className="border border-white-blue rounded-[8px] overflow-x-auto">
          <table className="w-full">
            <thead className="bg-bg">
              <tr>
                <th className="py-2 px-4 text-left paragraph-11 bg-light-grey text-dark-grey">Enable</th>
                <th className="py-2 px-4 text-left paragraph-11 bg-light-grey text-dark-grey">Automation Name</th>
                <th className="py-2 px-4 text-left paragraph-11 bg-light-grey text-dark-grey">Parameters used</th>
                <th className="py-2 px-4 text-left paragraph-11 bg-light-grey text-dark-grey">Blueprint</th>
                <th className="py-2 px-4 text-left paragraph-11 bg-light-grey text-dark-grey">Used by Profiles</th>
                <th className="py-2 px-4 text-left paragraph-11 bg-light-grey text-dark-grey">Created by</th>
                <th className="py-2 px-4 text-left paragraph-11 bg-light-grey text-dark-grey">Last Triggered</th>
                <th className="py-2 px-4 bg-light-grey"></th>
              </tr>
            </thead>
            <tbody>
              {automations.map((automation, idx) => (
                <tr
                  key={automation.id}
                  className={cn(
                    "border-t border-light-grey bg-white hover:bg-bg/50 hover:cursor-pointer",
                    idx % 2 === 1 && "bg-bg",
                  )}
                  onClick={() => navigate(`/settings/automations/${automation.id}`)}
                >
                  <td className="px-4 border-b border-light-grey" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <Switch
                        checked={automation.enabled}
                        onChange={(e) => {
                          const newAutomations = [...automations]
                          newAutomations[idx].enabled = e
                          setAutomations(newAutomations)
                          handleStatusChange(automation.id, e, idx)
                        }}
                        className={cn(
                          "relative inline-flex h-4 w-8 items-center rounded-full",
                          automation.enabled ? "bg-gradient-to-r from-primary to-green" : "bg-gray-200"
                        )}
                      >
                        <span
                          className={cn(
                            "inline-block h-3 w-3 transform rounded-full bg-white transition",
                            automation.enabled ? "translate-x-[18px]" : "translate-x-0.5"
                          )}
                        />
                      </Switch>
                      <p className="paragraph-8 text-[#5E5E5E]">{automation.enabled ? "Active" : "Inactive"}</p>
                    </div>
                  </td>
                  <td className={cn("heading-7 px-4 text-dark-blue", !automation.enabled && "text-[#B4B4B4]")}>{automation.name}</td>
                  <td className="py-2 px-4">
                    <div className="flex flex-wrap gap-1">
                      {automation.parameters?.map((param) => (
                        <ParameterTag key={param} parameter={param} noText={true} />
                      ))}
                    </div>
                  </td>
                  <td className="px-4"><TableTag text={automation.blueprint} /></td>
                  <td className="px-4">
                    <div className="flex items-center gap-1 flex-wrap">
                      {automation.profiles.map((v, index) => <TableTag key={index} text={v} />)}
                    </div>
                  </td>
                  <td className="paragraph-12 text-black px-4">
                    <div className='flex gap-1 items-center'>
                      <CircleUser color={COLOR.primary.primary} className="h-4 w-4" />
                      <p className="paragraph-12 text-black">{automation.createdBy}</p>
                    </div>
                  </td>
                  <td className="paragraph-12 text-black px-4">{automation.lastTriggered}</td>
                  <td className="py-2 px-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent onClick={(e) => e.stopPropagation()} align="end">
                        <DropdownMenuItem onClick={() => navigate(`/settings/automations/edit/${automation.id}`)}>
                          <Edit className="h-4 w-4 mr-2" />
                          <p className="paragraph-11 text-[#5E5E5E]">Edit</p>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDuplicate(automation.id)}>
                          <Copy className="h-4 w-4 mr-2" />
                          <p className="paragraph-11 text-[#5E5E5E]">Duplicate</p>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleExport(automation.export)}>
                          <FileExport className="h-4 w-4 mr-2" />
                          <p className="paragraph-11 text-[#5E5E5E]">Export</p>
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(automation.id)}>
                          <Trash2 className="h-4 w-4 mr-2" />
                          <p className="paragraph-11">Delete</p>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}